import * as React from 'react';
import { createPortal } from 'react-dom';

import clsx from 'clsx';

import '@app/scss/components/modal.scss';

import { Icon } from '@image/icon';

export interface ModalProps {
	open: boolean;
	className?: string;
	style?: React.CSSProperties;
	lockBodyScroll?: boolean;
	allowBackgroundScroll?: boolean;
	onClose: (value: boolean) => void;
	content: React.ReactNode;
}

const OVERLAY_STYLE: React.CSSProperties = {
	position: 'fixed',
	inset: 0,
	zIndex: 3000,
	background: 'rgba(9, 14, 44, 0.75)',
};

const CONTAINER_BASE_STYLE: React.CSSProperties = {
	position: 'absolute',
	top: '50%',
	left: '50%',
	transform: 'translate(-50%, -50%)',
	maxHeight: 'calc(100vh - 32px)',
	overflow: 'auto',
	background: '#fff',
	borderRadius: 5,
};

export const Modal: React.FC<ModalProps> = (props: ModalProps) => {
	const lockBodyScroll = props.lockBodyScroll ?? true;

	React.useEffect(() => {
		if (!lockBodyScroll) return undefined;

		document.body.classList.toggle('no-scroll-body', props.open);

		return () => {
			document.body.classList.remove('no-scroll-body');
		};
	}, [lockBodyScroll, props.open]);

	if (!props.open) return null;

	return createPortal(
		<div className={clsx('ovc-modal', props.allowBackgroundScroll && 'ovc-modal--allow-background-scroll')} style={OVERLAY_STYLE}>
			<div
				className="ovc-modal__overlay"
				style={{ position: 'absolute', inset: 0 }}
				onClick={() => props.onClose(false)}
			/>
			<div
				className={clsx('ovc-modal__container', props.className)}
				style={{ ...CONTAINER_BASE_STYLE, ...props.style }}
			>
				<button
					type="button"
					onClick={() => props.onClose(false)}
					className="ovc-modal__btn-close"
				>
					<Icon.Clear />
				</button>
				{props.content}
			</div>
		</div>,
		document.body,
	);
};
